import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "react-oidc-context";

export const useRoomData = (hotelId, roomId) => {
  const [room, setRoom] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [modules, setModules] = useState([]);
  const [showers, setShowers] = useState([]);
  const [heartbeats, setHeartbeats] = useState([]);
  const [batteryHistory, setBatteryHistory] = useState([]);
  const [statusLogs, setStatusLogs] = useState([]);
  const [moduleLogs, setModuleLogs] = useState([]);
  const [currentBattery, setCurrentBattery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const auth = useAuth();
  const token = auth.user?.access_token;
  const API_URL = process.env.REACT_APP_API_URL || "/api";

  // Refs pour éviter les boucles infinies
  const lastFetchRef = useRef(0);
  const isInitializedRef = useRef(false);

  const fetchRoomData = useCallback(async () => {
    if (!token || !hotelId || !roomId) return;

    // Éviter les appels trop fréquents (minimum 1 seconde entre les appels)
    const now = Date.now();
    if (now - lastFetchRef.current < 1000) {
      return;
    }
    lastFetchRef.current = now;

    try {
      setLoading(true);
      setError(null);

      // Récupération parallèle des données de la chambre et de l'hôtel
      const [roomResponse, hotelResponse] = await Promise.all([
        axios.get(`${API_URL}/hotels/${hotelId}/rooms/${roomId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/hotels/${hotelId}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      const enrichedRoom = roomResponse.data.result;
      const hotelData = hotelResponse.data.result;

      // Mise à jour des états
      setRoom(enrichedRoom.basic_info);
      setHotel(hotelData);
      setModules(enrichedRoom.module ? [enrichedRoom.module] : []);
      setShowers(enrichedRoom.showers || []);
      setHeartbeats(enrichedRoom.heartbeats || []);
      setBatteryHistory(enrichedRoom.battery_history || []);
      setStatusLogs(enrichedRoom.status_logs || []);
      setModuleLogs(enrichedRoom.module_logs || []);
      setCurrentBattery(enrichedRoom.current_battery || null);
    } catch (err) {
      console.error("Erreur lors de la récupération des données de la chambre:", err);
      setError(err.response?.data?.message || "Erreur lors du chargement des données de la chambre");
    } finally {
      setLoading(false);
    }
  }, [token, hotelId, roomId, API_URL]);

  const updateRoom = async (updateData) => {
    if (!token || !hotelId || !roomId) return { error: "Paramètres manquants" };

    try {
      const response = await axios.patch(`${API_URL}/hotels/${hotelId}/rooms/${roomId}`, updateData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Mise à jour locale optimiste
      if (response.data.result) {
        setRoom((prevRoom) => ({
          ...prevRoom,
          ...updateData,
        }));
      }

      return { success: true, data: response.data };
    } catch (err) {
      console.error("Erreur lors de la mise à jour de la chambre:", err);
      return { error: err.response?.data?.message || "Erreur lors de la mise à jour" };
    }
  };

  // Chargement initial des données - SEULEMENT au premier chargement
  useEffect(() => {
    if (!isInitializedRef.current && token && hotelId && roomId) {
      fetchRoomData();
      isInitializedRef.current = true;
    }
  }, [hotelId, roomId, token, fetchRoomData]);

  return {
    room,
    hotel,
    modules,
    showers,
    heartbeats,
    batteryHistory,
    statusLogs,
    moduleLogs,
    currentBattery,
    loading,
    error,
    updateRoom,
    refetch: () => {
      fetchRoomData();
    },
  };
};
